import type { ShapeNode } from "../builder";
import type { LibraryTemplate } from "../library";
import {
  BLACK,
  captions,
  cornerLogo,
  during,
  ellipse,
  enter,
  icon,
  logo,
  ON_VIDEO,
  parts,
  pill,
  REEL_CAPTIONS,
  rect,
  stack,
  star,
  t,
  text,
} from "./shorthands";

/**
 * More reels (9:16) straight on the video: formats that work for talking
 * heads, b-roll and screen recordings. Several have a twin with a device
 * mockup in `reel-mockups.ts` (website launch, screen tutorial).
 */

/** Light card at the top of the video, below the app's top bar. */
const topCard = (h: number, extra: Partial<Omit<ShapeNode, "kind" | "shape">> = {}) =>
  rect({ x: 60, y: 240, w: 960, h, fill: "background/92", radius: "lg", name: "Karte", ...extra });

export const MORE_REELS_2: LibraryTemplate[] = [
  {
    id: "reel-mythos-fakt",
    title: "Reel: Mythos oder Fakt?",
    description:
      "Erst der Irrtum mit „FALSCH“-Stempel, dann die Wahrheit mit Haken – mit Untertiteln",
    presetId: "reel",
    topic: "wissen",
    style: "klar",
    tags: ["mythos", "fakt", "irrtum", "wahrheit", "aufklärung", "faktencheck", "stimmt das"],
    previewTime: 3200,
    pages: [
      {
        background: BLACK,
        nodes: [
          topCard(340, enter("slide-down")),
          logo({
            x: 760,
            y: 272,
            w: 220,
            h: 56,
            on: "background",
            align: "right",
            ...enter("slide-down"),
          }),
          icon({
            icon: "x-circle",
            x: 100,
            y: 270,
            w: 60,
            h: 60,
            color: "text-muted",
            name: "Kreuz",
            ...during(0, 5000, "pop"),
          }),
          text({
            text: "MYTHOS",
            style: "subline",
            size: 34,
            color: "text-muted",
            x: 178,
            y: 270,
            w: 400,
            h: 60,
            ...during(0, 5000, "fade"),
          }),
          text({
            text: "Man muss jeden Tag posten, um zu wachsen",
            style: "heading-2",
            size: 60,
            color: "text",
            x: 100,
            y: 350,
            w: 880,
            h: 200,
            ...during(0, 5000, "slide-up"),
          }),
          rect({
            x: 560,
            y: 470,
            w: 380,
            h: 110,
            radius: "sm",
            rotation: -8,
            stroke: { color: "accent", width: 8 },
            name: "Stempel",
            ...during(2500, 5000, "zoom", { duration: 250 }),
          }),
          text({
            text: "FALSCH",
            style: "heading-1",
            size: 72,
            color: "accent",
            x: 560,
            y: 470,
            w: 380,
            h: 110,
            align: "center",
            rotation: -8,
            ...during(2500, 5000, "zoom", { duration: 250 }),
          }),
          icon({
            icon: "check-circle",
            x: 100,
            y: 270,
            w: 60,
            h: 60,
            color: "primary",
            name: "Haken",
            ...during(5000, 12000, "pop", { out: null }),
          }),
          text({
            text: "FAKT",
            style: "subline",
            size: 34,
            color: "primary",
            x: 178,
            y: 270,
            w: 400,
            h: 60,
            ...during(5000, 12000, "fade", { out: null }),
          }),
          text({
            text: "Zwei gute Beiträge pro Woche bringen mehr als sieben schnelle",
            style: "heading-2",
            size: 54,
            color: "text",
            x: 100,
            y: 350,
            w: 880,
            h: 200,
            ...during(5000, 12000, "slide-up", { out: null }),
          }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-top-5",
    title: "Reel: Top 5 (Ranking)",
    description: "Platz 5 bis 1 bauen sich nacheinander auf, Platz 1 mit Krone – dazu Untertitel",
    presetId: "reel",
    topic: "produkt",
    style: "laut",
    tags: ["top 5", "ranking", "bestseller", "liste", "favoriten", "hitliste", "platz 1"],
    previewTime: 12500,
    pages: [
      {
        background: BLACK,
        nodes: [
          ...pill(70, 250, 420, 72, "UNSERE TOP 5", "primary", 30, enter("slide-down")),
          cornerLogo(),
          text({
            text: "Das habt ihr im Oktober am liebsten gekauft",
            style: "heading-2",
            size: 50,
            x: 70,
            y: 340,
            w: 900,
            ...ON_VIDEO,
            ...enter("slide-down", { duration: 700 }),
          }),
          ...["Zimtschnecke", "Laugenbrezel", "Kürbisbrot", "Apfelstrudel", "Sauerteig-Laib"]
            .map((name, index) => ({ name, rank: index + 1 }))
            .flatMap(({ name, rank }) => {
              const y = 470 + (rank - 1) * 122;
              // Place 5 comes first, place 1 last.
              const motion = during(1500 + (5 - rank) * 2500, 16000, "slide-left", {
                duration: 450,
                out: null,
              });
              const top = rank === 1;
              return [
                ellipse({
                  x: 70,
                  y,
                  w: 100,
                  h: 100,
                  fill: top ? "accent" : "primary",
                  name: `Platz ${rank}`,
                  ...motion,
                }),
                text({
                  text: String(rank),
                  style: "heading-1",
                  size: 60,
                  color: top ? "on:accent" : "on:primary",
                  x: 70,
                  y,
                  w: 100,
                  h: 100,
                  align: "center",
                  ...motion,
                }),
                rect({
                  x: 190,
                  y,
                  w: 820,
                  h: 100,
                  fill: top ? "accent" : "background/92",
                  radius: "md",
                  name: `Eintrag ${rank}`,
                  ...motion,
                }),
                text({
                  text: name,
                  style: "heading-2",
                  size: 46,
                  color: top ? "on:accent" : "text",
                  x: 225,
                  y,
                  w: top ? 640 : 760,
                  h: 100,
                  ...motion,
                }),
                ...(top
                  ? [
                      icon({
                        icon: "crown",
                        x: 900,
                        y: y + 18,
                        w: 64,
                        h: 64,
                        color: "on:accent",
                        name: "Krone",
                        ...during(1500 + 4 * 2500, 16000, "pop", { out: null, loop: "wiggle" }),
                      }),
                    ]
                  : []),
              ];
            }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-ein-tag-bei-uns",
    title: "Reel: Ein Tag bei uns",
    description: "Vlog mit Uhrzeit-Einblendungen von morgens bis Feierabend, dazu Untertitel",
    presetId: "reel",
    topic: "team",
    style: "warm",
    tags: [
      "vlog",
      "tagesablauf",
      "ein tag",
      "alltag",
      "hinter den kulissen",
      "uhrzeit",
      "arbeitsalltag",
    ],
    previewTime: 1200,
    pages: [
      {
        background: BLACK,
        nodes: [
          ...pill(70, 250, 440, 72, "EIN TAG BEI UNS", "primary", 30, enter("slide-down")),
          cornerLogo(),
          ...parts(5, 500, 3000).flatMap(({ index, start, end }) => {
            const entries: [string, string][] = [
              ["07:30", "Ofen an, der erste Teig kommt raus"],
              ["09:00", "Die Stammgäste sind da"],
              ["12:00", "Mittagsansturm!"],
              ["15:30", "Neue Rezepte ausprobieren"],
              ["18:00", "Feierabend – bis morgen"],
            ];
            const [time, label] = entries[index]!;
            return [
              ...pill(70, 360, 250, 100, time, "accent", 54, {
                radius: "md",
                ...during(start, end, "slide-right", { duration: 400, outDuration: 250 }),
              }),
              text({
                text: label,
                style: "heading-1",
                size: 76,
                x: 70,
                y: 490,
                w: 900,
                name: `Station ${index + 1}`,
                ...ON_VIDEO,
                ...during(start, end, "slide-up", { duration: 500, outDuration: 250 }),
              }),
            ];
          }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-dies-oder-das",
    title: "Reel: Dies oder das?",
    description: "Zwei Karten fliegen herein – A oder B? Aufruf zum Kommentieren",
    presetId: "reel",
    topic: "community",
    style: "verspielt",
    tags: ["umfrage", "abstimmung", "a oder b", "entweder oder", "kommentare", "interaktion"],
    pages: [
      {
        background: BLACK,
        nodes: [
          text({
            text: "Was passt besser zu dir?",
            style: "heading-1",
            size: 84,
            x: 70,
            y: 240,
            w: 940,
            align: "center",
            ...ON_VIDEO,
            ...enter("slide-down"),
          }),
          ...(
            [
              { x: 70, letter: "A", label: "Kaffee", fill: "primary", from: "fly-left" },
              { x: 570, letter: "B", label: "Tee", fill: "accent", from: "fly-right" },
            ] as const
          ).flatMap((side) => {
            const motion = enter(side.from, { duration: 700 });
            return [
              rect({
                x: side.x,
                y: 440,
                w: 440,
                h: 440,
                fill: side.fill,
                radius: "lg",
                name: `Karte ${side.letter}`,
                ...motion,
              }),
              stack({
                x: side.x + 30,
                y: 660,
                w: 380,
                anchor: "center",
                align: "center",
                gap: 6,
                items: [
                  t(side.letter, "heading-1", 180, `on:${side.fill}`, motion),
                  t(side.label, "heading-2", 60, `on:${side.fill}`, motion),
                ],
              }),
            ];
          }),
          ellipse({
            x: 470,
            y: 590,
            w: 140,
            h: 140,
            fill: "background",
            name: "oder",
            ...enter("pop", { duration: 1100 }),
          }),
          text({
            text: "oder",
            style: "subline",
            size: 34,
            color: "text",
            x: 470,
            y: 590,
            w: 140,
            h: 140,
            align: "center",
            ...enter("pop", { duration: 1100 }),
          }),
          ...pill(140, 940, 800, 100, "Schreib A oder B in die Kommentare", "background", 34, {
            ...enter("slide-up", { duration: 1400, loop: "pulse" }),
          }),
          logo({ x: 390, y: 1080, w: 300, h: 70, on: BLACK, align: "center" }),
        ],
      },
    ],
  },
  {
    id: "reel-dos-donts",
    title: "Reel: Do’s & Don’ts",
    description:
      "Karte oben wechselt zwischen „Don’t“ mit Kreuz und „Do“ mit Haken – mit Untertiteln",
    presetId: "reel",
    topic: "wissen",
    style: "nacht",
    tags: ["dos and donts", "do", "dont", "fehler", "richtig falsch", "tipps", "ratgeber"],
    previewTime: 1400,
    pages: [
      {
        background: BLACK,
        nodes: [
          topCard(360, enter("slide-down")),
          text({
            text: "HANDYVIDEOS · DO’S & DON’TS",
            style: "subline",
            size: 26,
            color: "text-muted",
            x: 100,
            y: 270,
            w: 640,
            h: 44,
            ...enter("slide-down"),
          }),
          logo({
            x: 780,
            y: 268,
            w: 200,
            h: 48,
            on: "background",
            align: "right",
            ...enter("slide-down"),
          }),
          ...parts(4, 800, 3500).flatMap(({ index, start, end }) => {
            const good = index % 2 === 1;
            const lines = [
              "Nur mit dem Handy-Mikro filmen",
              "Ein Ansteckmikro für klaren Ton",
              "Mit dem Fenster im Rücken filmen",
              "Licht von vorne – zum Fenster schauen",
            ];
            const color = good ? "primary" : "accent";
            const motion = during(start, end, "slide-left", { duration: 400, outDuration: 250 });
            return [
              icon({
                icon: good ? "check-circle" : "x-circle",
                x: 100,
                y: 336,
                w: 64,
                h: 64,
                color,
                name: good ? "Haken" : "Kreuz",
                ...during(start, end, "pop", { duration: 400, outDuration: 250 }),
              }),
              text({
                text: good ? "DO" : "DON’T",
                style: "heading-2",
                size: 44,
                color,
                x: 182,
                y: 336,
                w: 400,
                h: 64,
                ...motion,
              }),
              text({
                text: lines[index]!,
                style: "heading-2",
                size: 54,
                color: "text",
                x: 100,
                y: 420,
                w: 880,
                h: 160,
                ...motion,
              }),
            ];
          }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-zitat",
    title: "Reel: Zitat",
    description: "Abgedunkeltes Video, großes Anführungszeichen und ein Zitat, das sich aufbaut",
    presetId: "reel",
    topic: "zitat",
    style: "traditionell",
    tags: ["zitat", "spruch", "motivation", "gedanke", "inspiration", "weisheit", "b-roll"],
    previewTime: 3000,
    pages: [
      {
        background: BLACK,
        nodes: [
          rect({
            x: 0,
            y: 0,
            w: 1080,
            h: 1920,
            fill: { type: "linear", angle: 180, stops: ["#00000099", "#00000040", "#000000a6"] },
            name: "Abdunklung",
          }),
          icon({
            icon: "quotes",
            x: 90,
            y: 380,
            w: 150,
            h: 150,
            color: "accent",
            name: "Anführungszeichen",
            ...enter("pop", { duration: 500 }),
          }),
          stack({
            x: 90,
            y: 560,
            w: 900,
            gap: 40,
            items: [
              t(
                "Gute Arbeit erkennt man daran, dass sie leicht aussieht.",
                "quote",
                80,
                undefined,
                {
                  ...ON_VIDEO,
                  ...enter("typewriter", { duration: 2200 }),
                },
              ),
              t(
                "— Maria Keller, Gründerin",
                "subline",
                36,
                "accent",
                enter("fade", { duration: 2800 }),
              ),
            ],
          }),
          logo({ x: 90, y: 1280, w: 260, h: 70, on: BLACK, ...enter("fade", { duration: 3000 }) }),
        ],
      },
    ],
  },
  {
    id: "reel-gewinnspiel",
    title: "Reel: Gewinnspiel",
    description:
      "Großes „Gewinnspiel“, der Preis, drei Teilnahme-Schritte nacheinander und die Frist",
    presetId: "reel",
    topic: "angebot",
    style: "verspielt",
    tags: ["gewinnspiel", "verlosung", "giveaway", "gewinnen", "preis", "teilnahme", "aktion"],
    previewTime: 6500,
    pages: [
      {
        background: BLACK,
        nodes: [
          ...(
            [
              { x: 90, y: 250, size: 70, duration: 900 },
              { x: 920, y: 300, size: 90, duration: 1100 },
              { x: 860, y: 560, size: 56, duration: 1300 },
            ] as const
          ).map((spark) =>
            icon({
              icon: "sparkle",
              x: spark.x,
              y: spark.y,
              w: spark.size,
              h: spark.size,
              color: "accent",
              name: "Funkeln",
              ...enter("pop", { duration: spark.duration, loop: "wiggle" }),
            }),
          ),
          text({
            text: "GEWINNSPIEL",
            style: "heading-1",
            size: 130,
            x: 60,
            y: 330,
            w: 960,
            align: "center",
            shrink: true,
            ...ON_VIDEO,
            ...enter("zoom", { duration: 500 }),
          }),
          ...pill(140, 500, 800, 96, "Zu gewinnen: Brunch für zwei", "accent", 38, {
            ...enter("pop", { duration: 800 }),
          }),
          ...["Folge unserem Kanal", "Like dieses Video", "Markiere, wen du mitnimmst"].flatMap(
            (step, index) => {
              const y = 660 + index * 130;
              const motion = during(1500 + index * 2000, 18000, "slide-left", { out: null });
              return [
                ellipse({
                  x: 90,
                  y,
                  w: 100,
                  h: 100,
                  fill: "primary",
                  name: `Schritt ${index + 1}`,
                  ...motion,
                }),
                text({
                  text: String(index + 1),
                  style: "heading-1",
                  size: 56,
                  color: "on:primary",
                  x: 90,
                  y,
                  w: 100,
                  h: 100,
                  align: "center",
                  ...motion,
                }),
                text({
                  text: step,
                  style: "heading-2",
                  size: 50,
                  x: 220,
                  y,
                  w: 780,
                  h: 100,
                  ...ON_VIDEO,
                  ...motion,
                }),
              ];
            },
          ),
          rect({
            x: -40,
            y: 1080,
            w: 1160,
            h: 110,
            fill: "primary",
            rotation: -3,
            name: "Frist",
            ...during(7500, 18000, "fly-right", { duration: 600, out: null }),
          }),
          text({
            text: "Teilnahme bis 12. Oktober, 23:59 Uhr",
            style: "subline",
            size: 40,
            color: "on:primary",
            x: 60,
            y: 1080,
            w: 960,
            h: 110,
            rotation: -3,
            align: "center",
            ...during(7500, 18000, "fly-right", { duration: 600, out: null }),
          }),
          logo({ x: 390, y: 1260, w: 300, h: 70, on: BLACK, align: "center" }),
        ],
      },
    ],
  },
  {
    id: "reel-standort",
    title: "Reel: So findest du uns",
    description:
      "Hüpfende Stecknadel, Adresse und Öffnungszeiten auf einer Karte – mit Untertiteln",
    presetId: "reel",
    topic: "ankuendigung",
    style: "natur",
    tags: [
      "standort",
      "adresse",
      "anfahrt",
      "laden",
      "eröffnung",
      "öffnungszeiten",
      "café",
      "geschäft",
    ],
    pages: [
      {
        background: BLACK,
        nodes: [
          logo({ x: 70, y: 250, w: 240, h: 66, on: BLACK }),
          icon({
            icon: "map-pin",
            x: 460,
            y: 330,
            w: 160,
            h: 160,
            color: "primary",
            name: "Stecknadel",
            shadow: { y: 8, blur: 24, color: "#00000080" },
            ...enter("drop", { duration: 700, loop: "float" }),
          }),
          text({
            text: "So findest du uns",
            style: "heading-1",
            size: 92,
            x: 70,
            y: 510,
            w: 940,
            align: "center",
            ...ON_VIDEO,
            ...enter("slide-up", { duration: 600 }),
          }),
          rect({
            x: 90,
            y: 700,
            w: 900,
            h: 320,
            fill: "background/94",
            radius: "lg",
            name: "Adresse",
            ...enter("slide-up", { duration: 900 }),
          }),
          stack({
            x: 130,
            y: 860,
            w: 820,
            anchor: "center",
            align: "center",
            gap: [10, 22],
            items: [
              t("Café Sonnenschein", "heading-2", 56, "text", enter("slide-up", { duration: 900 })),
              t(
                "Lindenstraße 12 · 04109 Leipzig",
                "body",
                36,
                "text",
                enter("slide-up", { duration: 900 }),
              ),
              t(
                "Mo–Sa 8–18 Uhr · So 9–14 Uhr",
                "subline",
                30,
                "primary",
                enter("slide-up", { duration: 900 }),
              ),
            ],
          }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-meilenstein",
    title: "Reel: Meilenstein & Danke",
    description: "Große Zahl, Herz und Konfetti – für Follower-Rekorde, Jubiläen und Danke-Videos",
    presetId: "reel",
    topic: "community",
    style: "laut",
    tags: ["danke", "follower", "meilenstein", "jubiläum", "geburtstag", "feiern", "rekord"],
    pages: [
      {
        background: BLACK,
        nodes: [
          ...(
            [
              { x: 110, y: 290, w: 40, fill: "accent", duration: 700 },
              { x: 890, y: 260, w: 56, fill: "primary", duration: 900 },
              { x: 170, y: 880, w: 64, fill: "primary", duration: 1100 },
              { x: 880, y: 820, w: 36, fill: "accent", duration: 1300 },
              { x: 60, y: 610, w: 30, fill: "background", duration: 1000 },
              { x: 980, y: 560, w: 44, fill: "accent", duration: 800 },
            ] as const
          ).map((dot) =>
            ellipse({
              x: dot.x,
              y: dot.y,
              w: dot.w,
              h: dot.w,
              fill: dot.fill,
              name: "Konfetti",
              ...enter("pop", { duration: dot.duration, loop: "float" }),
            }),
          ),
          star({
            x: 780,
            y: 380,
            w: 120,
            h: 120,
            sides: 5,
            innerRatio: 0.5,
            rotation: 12,
            fill: "accent",
            name: "Stern",
            ...enter("spin", { duration: 900, loop: "wiggle" }),
          }),
          text({
            text: "10.000",
            style: "heading-1",
            size: 250,
            x: 60,
            y: 440,
            w: 960,
            align: "center",
            shrink: true,
            ...ON_VIDEO,
            ...enter("zoom", { duration: 600 }),
          }),
          text({
            text: "Follower – danke euch!",
            style: "heading-2",
            size: 70,
            x: 60,
            y: 730,
            w: 960,
            align: "center",
            ...ON_VIDEO,
            ...enter("slide-up", { duration: 900 }),
          }),
          icon({
            icon: "heart",
            x: 480,
            y: 250,
            w: 120,
            h: 120,
            color: "primary",
            name: "Herz",
            ...enter("pop", { duration: 700, loop: "pulse" }),
          }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-team-vorstellung",
    title: "Reel: Lerne unser Team kennen",
    description:
      "Bauchbinde mit Name und Rolle, oben drei Fun Facts nacheinander – mit Untertiteln",
    presetId: "reel",
    topic: "team",
    style: "klar",
    tags: ["team", "mitarbeiter", "kollegin", "vorstellung", "azubi", "fun facts", "bauchbinde"],
    previewTime: 2000,
    pages: [
      {
        background: BLACK,
        nodes: [
          ...parts(3, 1500, 4000).flatMap(({ index, start, end }) => {
            const facts = [
              "Seit 2023 im Team",
              "Trinkt vier Kaffee am Tag",
              "Kennt jedes Logo auswendig",
            ];
            const motion = during(start, end, "slide-down", { duration: 450, outDuration: 250 });
            return [
              topCard(170, { name: `Fun Fact ${index + 1}`, ...motion }),
              stack({
                x: 100,
                y: 325,
                w: 880,
                anchor: "center",
                gap: 6,
                items: [
                  t(`FUN FACT ${index + 1}`, "subline", 26, "primary", motion),
                  t(facts[index]!, "heading-2", 52, "text", motion),
                ],
              }),
            ];
          }),
          rect({
            x: 60,
            y: 900,
            w: 700,
            h: 140,
            fill: "primary",
            radius: "md",
            name: "Bauchbinde",
            ...enter("slide-right", { duration: 600 }),
          }),
          stack({
            x: 95,
            y: 970,
            w: 630,
            anchor: "center",
            gap: 4,
            items: [
              t(
                "Jonas Weber",
                "heading-2",
                52,
                "on:primary",
                enter("slide-right", { duration: 600 }),
              ),
              t(
                "Azubi Mediengestaltung",
                "caption",
                30,
                "on:primary/80",
                enter("slide-right", { duration: 800 }),
              ),
            ],
          }),
          cornerLogo(),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-hook-abspann",
    title: "Reel: Hook & Abspann",
    description:
      "Hook-Balken in den ersten Sekunden, Untertitel, am Ende eine Karte mit Logo und „Folgen“",
    presetId: "reel",
    topic: "community",
    style: "nacht",
    tags: ["abspann", "outro", "endcard", "folgen", "abonnieren", "hook", "kanal"],
    previewTime: 13500,
    pages: [
      {
        background: BLACK,
        nodes: [
          rect({
            x: 0,
            y: 300,
            w: 1080,
            h: 260,
            fill: "primary",
            name: "Hook-Balken",
            ...during(0, 3500, "fly-left", { duration: 450 }),
          }),
          text({
            text: "Warte bis zum Ende",
            style: "heading-1",
            size: 88,
            color: "on:primary",
            x: 60,
            y: 300,
            w: 960,
            h: 260,
            align: "center",
            ...during(0, 3500, "fly-left", { duration: 450 }),
          }),
          captions(REEL_CAPTIONS),
          rect({
            x: 0,
            y: 0,
            w: 1080,
            h: 1920,
            fill: "primary",
            name: "Abspann",
            ...during(12000, 15000, "fade", { duration: 400, out: null }),
          }),
          logo({
            x: 290,
            y: 560,
            w: 500,
            h: 140,
            on: "primary",
            align: "center",
            ...during(12000, 15000, "zoom", { duration: 600, out: null }),
          }),
          text({
            text: "Folge für mehr",
            style: "heading-1",
            size: 110,
            color: "on:primary",
            x: 60,
            y: 780,
            w: 960,
            align: "center",
            ...during(12200, 15000, "slide-up", { duration: 600, out: null }),
          }),
          icon({
            icon: "bell-ringing",
            x: 470,
            y: 1010,
            w: 140,
            h: 140,
            color: "accent",
            name: "Glocke",
            ...during(12500, 15000, "pop", { duration: 500, out: null, loop: "wiggle" }),
          }),
        ],
      },
    ],
  },
  {
    id: "reel-preisliste",
    title: "Reel: Preise & Leistungen",
    description: "Drei Leistungen mit Preis erscheinen nacheinander, danach der Termin-Button",
    presetId: "reel",
    topic: "angebot",
    style: "warm",
    tags: [
      "preise",
      "preisliste",
      "leistungen",
      "friseur",
      "salon",
      "kosmetik",
      "termin",
      "handwerk",
    ],
    previewTime: 7500,
    pages: [
      {
        background: BLACK,
        nodes: [
          topCard(190, enter("slide-down")),
          logo({ x: 100, y: 262, w: 200, h: 44, on: "background", ...enter("slide-down") }),
          text({
            text: "Unsere Preise im Überblick",
            style: "heading-2",
            size: 56,
            color: "text",
            x: 100,
            y: 314,
            w: 880,
            h: 96,
            ...enter("slide-down"),
          }),
          ...(
            [
              ["Damenhaarschnitt", "49 €"],
              ["Herrenhaarschnitt", "29 €"],
              ["Färben", "ab 59 €"],
            ] as const
          ).flatMap(([service, price], index) => {
            const y = 470 + index * 130;
            const motion = during(1200 + index * 1800, 16000, "slide-left", { out: null });
            return [
              rect({
                x: 60,
                y,
                w: 960,
                h: 110,
                fill: "background/92",
                radius: "md",
                name: service,
                ...motion,
              }),
              text({
                text: service,
                style: "heading-2",
                size: 46,
                color: "text",
                x: 100,
                y,
                w: 600,
                h: 110,
                ...motion,
              }),
              text({
                text: price,
                style: "heading-2",
                size: 46,
                color: "primary",
                x: 700,
                y,
                w: 280,
                h: 110,
                align: "right",
                ...motion,
              }),
            ];
          }),
          ...pill(240, 900, 600, 100, "Termin über den Link in der Bio", "accent", 34, {
            ...during(7000, 16000, "pop", { out: null, loop: "pulse" }),
          }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-zeitraffer",
    title: "Reel: Projekt im Zeitraffer",
    description:
      "„Tag 1“ bis „Tag 30“ mit Fortschrittsbalken, zum Schluss „Fertig!“ – für Umbauten",
    presetId: "reel",
    topic: "produkt",
    style: "traditionell",
    tags: [
      "zeitraffer",
      "baustelle",
      "handwerk",
      "renovierung",
      "umbau",
      "garten",
      "fortschritt",
      "projekt",
    ],
    previewTime: 5000,
    pages: [
      {
        background: BLACK,
        nodes: [
          text({
            text: "Unser Garten-Umbau",
            style: "heading-1",
            size: 84,
            x: 70,
            y: 400,
            w: 940,
            ...ON_VIDEO,
            ...enter("slide-up", { duration: 600 }),
          }),
          cornerLogo(),
          ...parts(4, 0, 3500).flatMap(({ index, start, end }) => {
            const days = ["TAG 1", "TAG 7", "TAG 14", "TAG 30"];
            const w = 220;
            return [
              rect({
                x: 70 + index * (w + 20),
                y: 350,
                w,
                h: 14,
                fill: "#ffffff59",
                radius: "full",
                name: "Fortschritt",
              }),
              rect({
                x: 70 + index * (w + 20),
                y: 350,
                w,
                h: 14,
                fill: "accent",
                radius: "full",
                name: `Fortschritt ${index + 1}`,
                ...during(start, 17000, "slide-right", { duration: 400, out: null }),
              }),
              ...pill(70, 250, 240, 76, days[index]!, "accent", 34, {
                radius: "md",
                ...during(start, end, "pop", { duration: 300, outDuration: 150 }),
              }),
            ];
          }),
          ...pill(70, 540, 380, 100, "Fertig!", "primary", 48, {
            ...during(14000, 17000, "pop", { duration: 450, out: null, loop: "pulse" }),
          }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-website-launch",
    title: "Reel: Neue Website",
    description: "„Neu online“ mit pulsierendem Punkt, Schlagzeile und Adresse – über deinem Video",
    presetId: "reel",
    topic: "ankuendigung",
    style: "klar",
    tags: ["website", "webseite", "relaunch", "online", "homepage", "launch", "internetseite"],
    pages: [
      {
        background: BLACK,
        nodes: [
          ...pill(70, 250, 300, 68, "NEU ONLINE", "primary", 28, enter("slide-right")),
          ellipse({
            x: 390,
            y: 272,
            w: 24,
            h: 24,
            fill: "primary",
            name: "Live-Punkt",
            ...enter("fade", { loop: "pulse" }),
          }),
          cornerLogo(),
          rect({
            x: 60,
            y: 350,
            w: 960,
            h: 240,
            fill: "background/94",
            radius: "md",
            name: "Schlagzeile",
            ...enter("slide-up", { duration: 600 }),
          }),
          text({
            text: "Unsere neue\nWebsite ist da",
            style: "heading-1",
            size: 76,
            color: "text",
            x: 100,
            y: 350,
            w: 880,
            h: 240,
            ...enter("slide-up", { duration: 600 }),
          }),
          icon({
            icon: "globe",
            x: 90,
            y: 634,
            w: 60,
            h: 60,
            color: "accent",
            name: "Globus",
            ...enter("pop", { duration: 1000 }),
          }),
          ...pill(170, 620, 560, 90, "www.beispiel.de", "accent", 38, {
            ...enter("pop", { duration: 1000, loop: "pulse" }),
          }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
  {
    id: "reel-bildschirm-anleitung",
    title: "Reel: Bildschirm-Anleitung",
    description: "Für Bildschirmaufnahmen: Schritt-Karte oben und ein Tipp-Hinweis, der mitwandert",
    presetId: "reel",
    topic: "wissen",
    style: "verspielt",
    tags: [
      "bildschirmaufnahme",
      "screen recording",
      "anleitung",
      "app",
      "tutorial",
      "schritte",
      "erklären",
    ],
    previewTime: 1000,
    pages: [
      {
        background: BLACK,
        nodes: [
          ...parts(4, 0, 4000).flatMap(({ index, start, end }) => {
            const steps = [
              "Einstellungen öffnen",
              "Auf „Profil“ tippen",
              "Neues Foto auswählen",
              "Speichern – fertig!",
            ];
            const spots = [
              { x: 820, y: 620 },
              { x: 300, y: 820 },
              { x: 620, y: 1000 },
              { x: 480, y: 700 },
            ];
            const motion = during(start, end, "slide-down", { duration: 400, outDuration: 250 });
            return [
              topCard(180, { name: `Schritt ${index + 1}`, ...motion }),
              stack({
                x: 100,
                y: 330,
                w: 880,
                anchor: "center",
                gap: 6,
                items: [
                  t(`SCHRITT ${index + 1} VON 4`, "subline", 26, "primary", motion),
                  t(steps[index]!, "heading-2", 56, "text", motion),
                ],
              }),
              ellipse({
                x: spots[index]!.x,
                y: spots[index]!.y,
                w: 120,
                h: 120,
                fill: "accent/45",
                stroke: { color: "accent", width: 6 },
                name: `Tipp-Hinweis ${index + 1}`,
                ...during(start + 300, end, "zoom", {
                  duration: 350,
                  outDuration: 200,
                  loop: "pulse",
                }),
              }),
            ];
          }),
          logo({ x: 790, y: 440, w: 220, h: 60, on: BLACK, align: "right" }),
          captions(REEL_CAPTIONS),
        ],
      },
    ],
  },
];
